import React from 'react';

import styled from 'styled-components';

import AddModelBooksPreviewPopover, {
    DENSE_INTENT_DELAY,
} from '@components/ActivePackages/AddModelBooksPreviewPopover';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/** Le tre varianti che portano book nel package, con l'icona che le identifica. */
const VARIANT_ICONS = {
    fastPackage: byPrefixAndName.fasl['truck-fast'],
    compCards: byPrefixAndName.fasl['image-polaroid-user'],
    favourites: byPrefixAndName.fasl['heart'],
};

const IconWrapper = styled.div`
    /* Sotto DRAWER_COMPACT_MAX_WIDTH le icone non stanno più su una riga: vanno su una
       griglia di due colonne (fast pack, comp card, favourites, books, new media). */
    display: ${(props) => (props.$compact ? 'grid' : 'flex')};
    grid-template-columns: ${(props) => (props.$compact ? 'repeat(2, 1fr)' : 'none')};
    align-items: center;
    justify-items: center;
    justify-content: center;
    /* Con la quinta icona (favourites) il gap sta largo solo nella colonna della tabella:
       sulle card del package e su mobile lo spazio è meno, e sborderebbe. */
    gap: ${(props) => (props.$compact ? '0.5rem' : props.$isMobile ? '0.75rem' : props.$small ? '0.75rem' : '1.5rem')};
    font-size: ${(props) => (props.$isMobile ? '1.5rem' : props.$small ? '1.5rem' : '2rem')};

    .ant-badge {
        font-size: ${(props) => (props.$isMobile ? '1.5rem' : props.$small ? '1.5rem' : '2rem')};
        .fa-lg {
            font-size: ${(props) => (props.$isMobile ? '1.5rem' : props.$small ? '1.5rem' : '2rem')};
        }
        sup.ant-badge-count {
            span {
                font-size: ${(props) => (props.$isMobile ? '.8rem' : props.$small ? '.8rem' : 'rem')};
            }
        }
    }
`;

const PackageActionIcons = ({
    model,
    isMobile,
    packageActions,
    drawerActions,
    onOpenDrawer, // Solo per mobile
    noTooltip = false, // Solo per mobile
    small = false,
    compact = false, // Drawer package sotto DRAWER_COMPACT_MAX_WIDTH: icone su griglia a 2 colonne
}) => {
    const {
        alreadyInPackage,
        addFastPack,
        addCompCards,
        addFavourites,
        compCardsInPackage,
        favouritesInPackage,
        getModelGroupConflict,
        isAddingFastPack,
        isAddingCompCards,
        isAddingFavourites,
        getPlaylistCount,
    } = packageActions;
    const { openBooksDrawer, handleOpenAddMediaDrawer } = drawerActions;

    const hasFastPack = model.FP && model.FP.length > 0;
    const isInPackage = alreadyInPackage(model);

    const hasCompCards = model.CP && model.CP.length > 0;
    const areCompCardsInPackage = compCardsInPackage(model);

    const hasFavourites = model.FAV && model.FAV.length > 0;
    const areFavouritesInPackage = favouritesInPackage(model);

    // Modella già nel package sotto un altro gruppo: non è aggiungibile qui, e spegnere
    // tutto evita di arrivare al selettore di book con book che non si possono aggiungere.
    const groupConflict = getModelGroupConflict ? getModelGroupConflict(model) : null;
    const conflictTooltip = groupConflict ? `already in group "${groupConflict}"` : null;

    if (groupConflict) {
        return (
            <IconWrapper $isMobile={isMobile} $small={small} $compact={compact}>
                <NwIcon icon={VARIANT_ICONS.fastPackage} disabled tooltip={conflictTooltip} />
                <NwIcon icon={VARIANT_ICONS.compCards} disabled tooltip={conflictTooltip} />
                <NwIcon icon={VARIANT_ICONS.favourites} disabled tooltip={conflictTooltip} />
                <NwIcon icon={byPrefixAndName.fasl['images']} disabled tooltip={conflictTooltip} />
                <NwIcon icon={byPrefixAndName.fasl['folder-user']} disabled tooltip={conflictTooltip} />
            </IconWrapper>
        );
    }

    /**
     * Sul desktop l'anteprima prende il posto del tooltip: dice le stesse cose e in piu'
     * mostra i book. Su mobile l'hover non e' affidabile e resta il tooltip.
     */
    const withPreview = (variant, label, icon) => {
        if (isMobile) {
            return icon;
        }
        return (
            <AddModelBooksPreviewPopover
                variant={variant}
                modelId={model.ID}
                icon={VARIANT_ICONS[variant]}
                label={label}
                intentDelay={DENSE_INTENT_DELAY}
            >
                {icon}
            </AddModelBooksPreviewPopover>
        );
    };

    /** Il tooltip resta solo dove l'anteprima non c'e'. */
    const hoverLabel = (text) => (isMobile && !noTooltip ? text : null);

    return (
        <IconWrapper $isMobile={isMobile} $small={small} $compact={compact}>
            {isMobile && (
                <NwIcon
                    icon={byPrefixAndName.fasl['sidebar-flip']}
                    pointer
                    onClick={() => onOpenDrawer(model.ID, 'profile')}
                />
            )}
            {!hasFastPack ? (
                <NwIcon icon={VARIANT_ICONS.fastPackage} disabled primary tooltip={`fast pack (0)`} />
            ) : !isInPackage ? (
                withPreview(
                    'fastPackage',
                    `fast pack (${model.FP.length})`,
                    <NwIcon
                        loading={isAddingFastPack === model.ID}
                        icon={VARIANT_ICONS.fastPackage}
                        tooltip={hoverLabel(`fast pack (${model.FP.length})`)}
                        badge={model.FP.length}
                        pointer
                        primary
                        onClick={() => addFastPack(model.ID)}
                    />
                )
            ) : (
                withPreview(
                    'fastPackage',
                    'model already in package',
                    <NwIcon
                        icon={VARIANT_ICONS.fastPackage}
                        disabled
                        check
                        primary
                        tooltip={hoverLabel('model already in package')}
                    />
                )
            )}
            {!hasCompCards ? (
                <NwIcon icon={VARIANT_ICONS.compCards} disabled primary tooltip={`comp cards (0)`} />
            ) : !areCompCardsInPackage ? (
                withPreview(
                    'compCards',
                    `comp cards (${model.CP.length})`,
                    <NwIcon
                        loading={isAddingCompCards === model.ID}
                        icon={VARIANT_ICONS.compCards}
                        tooltip={hoverLabel(`comp cards (${model.CP.length})`)}
                        badge={model.CP.length}
                        pointer
                        primary
                        onClick={() => addCompCards(model)}
                    />
                )
            ) : (
                withPreview(
                    'compCards',
                    'comp cards already in package',
                    <NwIcon
                        icon={VARIANT_ICONS.compCards}
                        disabled
                        check
                        primary
                        tooltip={hoverLabel('comp cards already in package')}
                    />
                )
            )}
            {!hasFavourites ? (
                <NwIcon icon={VARIANT_ICONS.favourites} disabled primary tooltip={`favourites (0)`} />
            ) : !areFavouritesInPackage ? (
                withPreview(
                    'favourites',
                    `favourites (${model.FAV.length})`,
                    <NwIcon
                        loading={isAddingFavourites === model.ID}
                        icon={VARIANT_ICONS.favourites}
                        tooltip={hoverLabel(`favourites (${model.FAV.length})`)}
                        badge={model.FAV.length}
                        pointer
                        primary
                        onClick={() => addFavourites(model)}
                    />
                )
            ) : (
                withPreview(
                    'favourites',
                    'favourites already in package',
                    <NwIcon
                        icon={VARIANT_ICONS.favourites}
                        disabled
                        check
                        primary
                        tooltip={hoverLabel('favourites already in package')}
                    />
                )
            )}
            <NwIcon
                icon={byPrefixAndName.fasl['images']}
                tooltip={noTooltip ? null : 'select books'}
                primary
                pointer
                onClick={() => openBooksDrawer(model.ID)}
                badge={getPlaylistCount(model)}
            />
            <NwIcon
                icon={byPrefixAndName.fasl['folder-user']}
                tooltip={noTooltip ? null : 'create a new media selection for this package'}
                primary
                pointer
                onClick={() => handleOpenAddMediaDrawer(model.ID)}
            />
        </IconWrapper>
    );
};

export default PackageActionIcons;
